/**
 * Security Alerts Service
 * Automatically sends alerts to the notifications center and via email when:
 * - A suspicious login is detected
 * - A session is terminated
 * - Failed verification attempts are triggered
 */

import { supabase } from '../lib/supabase';

const TRANSACTIONAL_URL = `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-transactional-emails`;

// ─── HELPERS ──────────────────────────────────────────────────────────────────

const getAuthHeaders = async () => {
  try {
    const { data: sessionData } = await supabase?.auth?.getSession();
    const accessToken = sessionData?.session?.access_token;
    const headers = { 'Content-Type': 'application/json' };
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
    return headers;
  } catch {
    return { 'Content-Type': 'application/json' };
  }
};

/**
 * Insert a notification into the notifications table for the user.
 * This triggers the realtime hook and shows in the notifications center.
 */
const insertNotification = async ({ userId, type, title, message, metadata = {} }) => {
  if (!userId) return;
  try {
    await supabase?.from('notifications')?.insert({
      recipient_id: userId,
      type,
      title,
      message,
      is_read: false,
      metadata,
    });
  } catch (err) {
    console.warn('[SecurityAlerts] Failed to insert notification:', err?.message);
  }
};

/**
 * Send a security alert email via the transactional edge function.
 */
const sendSecurityEmail = async ({ userEmail, userName, alertType, details }) => {
  if (!userEmail) return;
  try {
    const headers = await getAuthHeaders();
    await fetch(TRANSACTIONAL_URL, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        type: 'security_alert',
        data: { user_email: userEmail, user_name: userName, alert_type: alertType, details },
      }),
    });
  } catch (err) {
    console.warn('[SecurityAlerts] Failed to send security email:', err?.message);
  }
};

// ─── PUBLIC API ───────────────────────────────────────────────────────────────

/**
 * Alert: Suspicious login detected.
 * Called when a login attempt is flagged as suspicious (failed attempts, unusual IP, etc.)
 * @param {object} params
 * @param {string} params.userId
 * @param {string} params.userEmail
 * @param {string} [params.userName]
 * @param {string} [params.ipAddress]
 * @param {string} [params.deviceName]
 * @param {string} [params.location]
 * @param {string} [params.browser]
 * @param {string} [params.failureReason]
 */
export const alertSuspiciousLogin = async ({
  userId,
  userEmail,
  userName,
  ipAddress,
  deviceName,
  location,
  browser,
  failureReason,
}) => {
  const details = [
    ipAddress && `IP : ${ipAddress}`,
    deviceName && `Appareil : ${deviceName}`,
    browser && `Navigateur : ${browser}`,
    location && `Localisation : ${location}`,
    failureReason && `Raison : ${failureReason}`,
  ]
    ?.filter(Boolean)
    ?.join(' · ');

  const message = `Une tentative de connexion suspecte a été détectée sur votre compte.${details ? ` ${details}` : ''}`;

  await Promise.allSettled([
    insertNotification({
      userId,
      type: 'security_alert',
      title: '⚠️ Connexion suspecte détectée',
      message,
      metadata: { alert_type: 'suspicious_login', ip_address: ipAddress, device_name: deviceName, browser },
    }),
    sendSecurityEmail({
      userEmail,
      userName,
      alertType: 'suspicious_login',
      details: { ip_address: ipAddress, device_name: deviceName, browser, location, failure_reason: failureReason },
    }),
  ]);
};

/**
 * Alert: Session terminated (revoked by user or auto-logout).
 * @param {object} params
 * @param {string} params.userId
 * @param {string} params.userEmail
 * @param {string} [params.userName]
 * @param {string} [params.deviceName]
 * @param {'manual'|'idle_timeout'|'revoked'} [params.reason]
 */
export const alertSessionTerminated = async ({
  userId,
  userEmail,
  userName,
  deviceName,
  reason = 'manual',
}) => {
  const reasonLabels = {
    manual: 'Déconnexion manuelle',
    idle_timeout: 'Expiration pour inactivité (30 min)',
    revoked: 'Session révoquée à distance',
  };
  const reasonLabel = reasonLabels?.[reason] || reason;
  const devicePart = deviceName ? ` sur ${deviceName}` : '';
  const message = `Votre session${devicePart} a été terminée. Raison : ${reasonLabel}.`;

  await Promise.allSettled([
    insertNotification({
      userId,
      type: 'security_alert',
      title: '🔒 Session terminée',
      message,
      metadata: { alert_type: 'session_terminated', device_name: deviceName, reason },
    }),
    sendSecurityEmail({
      userEmail,
      userName,
      alertType: 'session_terminated',
      details: { device_name: deviceName, reason: reasonLabel },
    }),
  ]);
};

/**
 * Alert: Failed verification attempt (email verification resend failure or repeated failures).
 * @param {object} params
 * @param {string} params.userId
 * @param {string} params.userEmail
 * @param {string} [params.userName]
 * @param {string} [params.attemptType] - e.g. 'email_verification', 'password_reset'
 * @param {string} [params.errorMessage]
 */
export const alertFailedVerification = async ({
  userId,
  userEmail,
  userName,
  attemptType = 'email_verification',
  errorMessage,
}) => {
  const typeLabels = {
    email_verification: 'Vérification email',
    password_reset: 'Réinitialisation mot de passe',
  };
  const typeLabel = typeLabels?.[attemptType] || attemptType;
  const errorPart = errorMessage ? ` Erreur : ${errorMessage}` : '';
  const message = `Une tentative de ${typeLabel?.toLowerCase()} a échoué sur votre compte.${errorPart}`;

  await Promise.allSettled([
    insertNotification({
      userId,
      type: 'security_alert',
      title: '❌ Tentative de vérification échouée',
      message,
      metadata: { alert_type: 'failed_verification', attempt_type: attemptType, error_message: errorMessage },
    }),
    sendSecurityEmail({
      userEmail,
      userName,
      alertType: 'failed_verification',
      details: { attempt_type: typeLabel, error_message: errorMessage },
    }),
  ]);
};
